import React from 'react';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDPrimaryActionButton, FDPrimaryActionIconButton, FDStack,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { DialogActions, DialogContent, DialogImage } from 'Views/Page/Content/Modal/Components';
import { noop } from 'Helpers/PropTypes';
import fr from 'facet-rest';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import { dataAttrs } from 'Helpers/Testing';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import metaOverrideSet from './metaOverrideSet';

const useStyles = buildUseStyles('PXTextModalWrapper', (theme) => ({
  topPadding: {
    paddingBottom: ({ isMobile }) => theme.spacing(isMobile ? 1 : 2),
  },
  imageGap: {
    paddingBottom: ({ isMobile }) => theme.spacing(isMobile ? 2 : 4),
  },
  titleGap: {
    paddingBottom: theme.spacing(2),
  },
}));

/**
 * This modal layout wrapper is meant to be used for PX modal layouts that have an image and some informational text
 * with no form input fields.
 */
const PXTextModalWrapper = ({
  children, buttonLabel, onSubmitSuccess, additionalActions, submitOverride,
}) => {
  const { onSubmit, saving, resource } = useMetaLayoutContext();
  const { loadModalCollectionByLink, clearModalCollection } = useModalContext();
  const { loadPageContentLink } = usePageContentContext();
  const isMobile = useMobileLayout();
  const classes = useStyles({ isMobile });

  const handleContinue = async () => {
    if (submitOverride) {
      await submitOverride();
      return;
    }

    onSubmitSuccess(await onSubmit());
  };

  return (
    <>
      <DialogContent scrollKey={resource?.type() + resource?.id()}>
        <FDStack direction={'column'} paddingX={4}>
          <div className={classes.topPadding} />
          {resource.withRelationship(fr.relationships.image.name, () => {
            return (
              <>
                <DialogImage value={resource} />
                <div className={classes.imageGap} />
              </>
            );
          }, () => {
            return null;
          })}
          <MetaControlField attr={'title'} resource={resource} metaOverrideSet={metaOverrideSet} {...dataAttrs('dialog-title', resource)} />
          <div className={classes.titleGap} />
          <FDStack direction="column" spacing={2}>
            <MetaControlField attr={'subtitle'} resource={resource} metaOverrideSet={metaOverrideSet} />
            {children}
          </FDStack>
        </FDStack>
      </DialogContent>
      <DialogActions>
        {resource.withRelationship(fr.relationships.previousLink.name,
          (rel) => (
            <FDPrimaryActionIconButton
              icon="chevronLeft"
              onClick={() => {
                loadModalCollectionByLink(rel.link('related').url());
              }}
            />
          ),
          () => null)}
        {additionalActions}
        {resource.withRelationship(fr.relationships.navigate.name,
          (rel) => (
            <FDPrimaryActionButton
              {...dataAttrs('navigate-btn', resource)}
              label={rel.meta('prompt')}
              onClick={async () => {
                await loadPageContentLink(rel.link('related'));
                clearModalCollection();
              }}
            />
          ),
          () => null)}
        {resource.meta().canPost() ? <FDPrimaryActionButton
          label={resource.attr('primaryActionLabel') || buttonLabel}
          onClick={handleContinue}
          disabled={saving}
          {...dataAttrs('continue-btn', resource)}
        /> : null}
      </DialogActions>
    </>
  );
};

PXTextModalWrapper.defaultProps = {
  onSubmitSuccess: noop,
  submitOverride: null,
  buttonLabel: 'Continue →',
  additionalActions: null,
};

PXTextModalWrapper.propTypes = {
  additionalActions: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  onSubmitSuccess: PropTypes.func,
  submitOverride: PropTypes.func,
  buttonLabel: PropTypes.string,
};

export default PXTextModalWrapper;
